/*
Purpose: Add a student enrolment from the Calendar (v2) toolbar.
*/

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Form, Modal, Row } from 'react-bootstrap';
import Button from '@mui/material/Button';
import Select from 'react-select';
import Moment from 'moment';

import { options_enrol_category } from '../../../global';
import {
  createStudentEnrolment,
  fetchEnrolmentSessionOptions,
  fetchProductOptions,
  fetchProductTypeOptions,
  fetchStudentOptions,
  fetchStudentPaymentVerify,
  fetchSubjectOptions,
} from '../calendar-api';

const PAYMENT_SERVICE_OPTIONS = [
  { value: '', label: '---Select---' },
  { value: '1', label: 'Prepaid' },
  { value: '2', label: 'Postpaid' },
];

const EMPTY_FORM = {
  fk_stu_key: '',
  fk_sub_id: [],
  enrol_category: 'In Centre',
  fk_sest_key: '',
  enrol_start_date: Moment().format('YYYY-MM-DD'),
  pp_pay_method: '',
  fk_pt_key: '',
  enrol_product: '',
  enrol_pay_serice: '',
  billing_start_date: '',
};

export default function AddEnrolmentModal({ show, onCancel, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [studentOptions, setStudentOptions] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [sessionOptions, setSessionOptions] = useState([{ value: '', label: '--Select--' }]);
  const [productTypeOptions, setProductTypeOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState([
    { value: '', label: '---Select---' },
    { value: '2', label: 'Offline' },
  ]);
  const [verifyData, setVerifyData] = useState(null);
  const [lookupsLoading, setLookupsLoading] = useState(false);

  const showPaymentFields = Boolean(form.pp_pay_method);

  useEffect(() => {
    if (!show) {
      return;
    }

    setForm({
      ...EMPTY_FORM,
      enrol_start_date: Moment().format('YYYY-MM-DD'),
    });
    setErrors({});
    setSubmitError('');
    setSubjectOptions([]);
    setSessionOptions([{ value: '', label: '--Select--' }]);
    setProductOptions([]);
    setVerifyData(null);

    let cancelled = false;
    setLookupsLoading(true);

    Promise.all([fetchStudentOptions(), fetchProductTypeOptions()])
      .then(([students, productTypes]) => {
        if (cancelled) {
          return;
        }
        setStudentOptions(students);
        setProductTypeOptions(productTypes);
      })
      .catch((error) => {
        console.error('Calendar: unable to load enrolment lookups', error);
      })
      .finally(() => {
        if (!cancelled) {
          setLookupsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [show]);

  useEffect(() => {
    if (!form.fk_stu_key) {
      setSubjectOptions([]);
      setSessionOptions([{ value: '', label: '--Select--' }]);
      setVerifyData(null);
      return;
    }

    let cancelled = false;

    Promise.all([
      fetchSubjectOptions(form.fk_stu_key),
      fetchEnrolmentSessionOptions({ studentKey: form.fk_stu_key }),
      fetchStudentPaymentVerify(form.fk_stu_key),
    ])
      .then(([subjects, sessions, payment]) => {
        if (cancelled) {
          return;
        }
        setSubjectOptions(subjects);
        setSessionOptions(sessions);
        setPaymentMethodOptions(payment.paymentMethodOptions);
        setVerifyData(payment.verifyData);
      })
      .catch((error) => {
        console.error('Calendar: unable to load student enrolment options', error);
      });

    return () => {
      cancelled = true;
    };
  }, [form.fk_stu_key]);

  useEffect(() => {
    if (!form.pp_pay_method || !form.fk_pt_key || !form.fk_stu_key) {
      setProductOptions([]);
      return;
    }

    let cancelled = false;

    fetchProductOptions({
      paymentMethod: form.pp_pay_method,
      productTypeKey: form.fk_pt_key,
      studentKey: form.fk_stu_key,
    })
      .then((products) => {
        if (!cancelled) {
          setProductOptions(products);
        }
      })
      .catch((error) => {
        console.error('Calendar: unable to load products', error);
      });

    return () => {
      cancelled = true;
    };
  }, [form.pp_pay_method, form.fk_pt_key, form.fk_stu_key]);

  function updateField(name, value) {
    setForm((previous) => {
      const next = { ...previous, [name]: value };
      if (name === 'fk_stu_key') {
        next.fk_sub_id = [];
        next.fk_sest_key = '';
        next.pp_pay_method = '';
        next.fk_pt_key = '';
        next.enrol_product = '';
        next.enrol_pay_serice = '';
        next.billing_start_date = '';
      }
      if (name === 'pp_pay_method' && !value) {
        next.fk_pt_key = '';
        next.enrol_product = '';
        next.enrol_pay_serice = '';
        next.billing_start_date = '';
      }
      if (name === 'fk_pt_key') {
        next.enrol_product = '';
      }
      return next;
    });
    setErrors((previous) => ({ ...previous, [name]: undefined }));
    setSubmitError('');
  }

  function validate() {
    const next = {};
    if (!form.fk_stu_key) next.fk_stu_key = 'Student is required.';
    if (!form.fk_sub_id || form.fk_sub_id.length === 0) {
      next.fk_sub_id = 'Subject is required.';
    }
    if (!form.enrol_category) next.enrol_category = 'Enrolment category is required.';
    if (!form.fk_sest_key) next.fk_sest_key = 'Session is required.';
    if (!form.enrol_start_date) next.enrol_start_date = 'Enrolment date is required.';

    if (form.pp_pay_method) {
      if (!form.fk_pt_key) next.fk_pt_key = 'Product type is required.';
      if (!form.enrol_product) next.enrol_product = 'Product is required.';
      if (!form.enrol_pay_serice) next.enrol_pay_serice = 'Payment service is required.';
      if (!form.billing_start_date) next.billing_start_date = 'Billing start date is required.';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await createStudentEnrolment(
        {
          fk_stu_key: form.fk_stu_key,
          fk_sub_id: form.fk_sub_id,
          sest_class: false,
          enrol_category: form.enrol_category,
          fk_sest_key: form.fk_sest_key,
          enrol_start_date: form.enrol_start_date,
          enrol_product: form.enrol_product || '',
          enrol_price: 0,
          enrol_reg_fees: 0,
          enrol_price_type: '',
          enrol_deposit: 0,
          pp_pay_method: form.pp_pay_method || '',
          fk_pt_key: form.fk_pt_key || '',
          enrol_pay_serice: form.enrol_pay_serice || '',
          billing_start_date: form.billing_start_date || '',
        },
        form.pp_pay_method ? verifyData : null
      );

      if (result.ok) {
        onCreated();
        return;
      }

      setSubmitError(
        result.message || 'Failure! Unable to create student enrolment. Try again later.'
      );
    } catch (error) {
      console.error('Calendar: create enrolment failed', error);
      setSubmitError('Failure! Unable to create student enrolment. Try again later.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal show={show} onHide={onCancel} size="lg" centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Add Enrolment</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {lookupsLoading ? (
            <p className="text-muted mb-0">Loading students…</p>
          ) : (
            <Row>
              <Col lg="12" className="mb-3">
                <h6 className="form-lable required-field">Student</h6>
                <Select
                  menuPlacement="auto"
                  options={studentOptions}
                  value={studentOptions.find((option) => option.value === form.fk_stu_key) || null}
                  onChange={(option) => updateField('fk_stu_key', option ? option.value : '')}
                  placeholder="Select student"
                />
                {errors.fk_stu_key ? <span className="error">{errors.fk_stu_key}</span> : null}
              </Col>

              <Col lg="6" className="mb-3">
                <h6 className="form-lable required-field">Subject</h6>
                <Select
                  isMulti
                  menuPlacement="auto"
                  options={subjectOptions}
                  value={form.fk_sub_id}
                  onChange={(options) => updateField('fk_sub_id', options || [])}
                  isDisabled={!form.fk_stu_key}
                  placeholder="Select subject(s)"
                />
                {errors.fk_sub_id ? <span className="error">{errors.fk_sub_id}</span> : null}
              </Col>

              <Col lg="6" className="mb-3">
                <h6 className="form-lable required-field">Enrolment category</h6>
                <Select
                  menuPlacement="auto"
                  options={options_enrol_category}
                  value={
                    options_enrol_category.find(
                      (option) => option.value === form.enrol_category
                    ) || null
                  }
                  onChange={(option) =>
                    updateField('enrol_category', option ? option.value : '')
                  }
                />
                {errors.enrol_category ? (
                  <span className="error">{errors.enrol_category}</span>
                ) : null}
              </Col>

              <Col lg="6" className="mb-3">
                <h6 className="form-lable required-field">Session</h6>
                <Select
                  menuPlacement="auto"
                  options={sessionOptions}
                  value={
                    sessionOptions.find((option) => option.value === form.fk_sest_key) || null
                  }
                  onChange={(option) => updateField('fk_sest_key', option ? option.value : '')}
                  isDisabled={!form.fk_stu_key}
                />
                {errors.fk_sest_key ? <span className="error">{errors.fk_sest_key}</span> : null}
              </Col>

              <Col lg="6" className="mb-3">
                <h6 className="form-lable required-field">Enrolment date</h6>
                <Form.Control
                  type="date"
                  value={form.enrol_start_date}
                  onChange={(event) => updateField('enrol_start_date', event.target.value)}
                />
                {errors.enrol_start_date ? (
                  <span className="error">{errors.enrol_start_date}</span>
                ) : null}
              </Col>

              <Col lg="6" className="mb-3">
                <h6 className="form-lable">Payment method</h6>
                <Select
                  menuPlacement="auto"
                  options={paymentMethodOptions}
                  value={
                    paymentMethodOptions.find((option) => option.value === form.pp_pay_method) ||
                    null
                  }
                  onChange={(option) =>
                    updateField('pp_pay_method', option ? option.value : '')
                  }
                  isDisabled={!form.fk_stu_key}
                />
              </Col>

              {showPaymentFields ? (
                <>
                  <Col lg="6" className="mb-3">
                    <h6 className="form-lable required-field">Product type</h6>
                    <Select
                      menuPlacement="auto"
                      options={productTypeOptions}
                      value={
                        productTypeOptions.find((option) => option.value === form.fk_pt_key) ||
                        null
                      }
                      onChange={(option) =>
                        updateField('fk_pt_key', option ? option.value : '')
                      }
                    />
                    {errors.fk_pt_key ? <span className="error">{errors.fk_pt_key}</span> : null}
                  </Col>

                  <Col lg="6" className="mb-3">
                    <h6 className="form-lable required-field">Product</h6>
                    <Select
                      menuPlacement="auto"
                      options={productOptions}
                      value={
                        productOptions.find((option) => option.value === form.enrol_product) ||
                        null
                      }
                      onChange={(option) =>
                        updateField('enrol_product', option ? option.value : '')
                      }
                      isDisabled={!form.fk_pt_key}
                    />
                    {errors.enrol_product ? (
                      <span className="error">{errors.enrol_product}</span>
                    ) : null}
                  </Col>

                  <Col lg="6" className="mb-3">
                    <h6 className="form-lable required-field">Payment service</h6>
                    <Select
                      menuPlacement="auto"
                      options={PAYMENT_SERVICE_OPTIONS}
                      value={
                        PAYMENT_SERVICE_OPTIONS.find(
                          (option) => option.value === form.enrol_pay_serice
                        ) || null
                      }
                      onChange={(option) =>
                        updateField('enrol_pay_serice', option ? option.value : '')
                      }
                    />
                    {errors.enrol_pay_serice ? (
                      <span className="error">{errors.enrol_pay_serice}</span>
                    ) : null}
                  </Col>

                  <Col lg="6" className="mb-3">
                    <h6 className="form-lable required-field">Billing start date</h6>
                    <Form.Control
                      type="date"
                      value={form.billing_start_date}
                      min={form.enrol_start_date || undefined}
                      onChange={(event) =>
                        updateField('billing_start_date', event.target.value)
                      }
                    />
                    {errors.billing_start_date ? (
                      <span className="error">{errors.billing_start_date}</span>
                    ) : null}
                  </Col>
                </>
              ) : null}
            </Row>
          )}

          {submitError ? <div className="error mt-2">{submitError}</div> : null}
        </Modal.Body>

        <Modal.Footer className="pt-0">
          <div className="mx-auto d-flex gap-2">
            <Button variant="contained" className="primary-gray" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              className="primary-filled"
              disabled={isSubmitting || lookupsLoading}
            >
              {isSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

AddEnrolmentModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};
